// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePersona } from '../../hooks/usePersona';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useApprovals } from '../../hooks/useApprovals';
import { HITL_SITE_ID, HITL_APPROVAL_TYPE } from '../../utils/constants';
import { railItemsFor } from './navModel';

/**
 * 56px left icon rail. Items are persona-gated; active state derives from the
 * current route prefix. Replaces the mock's `*NavBg` derived props.
 */
export default function IconRail() {
  const { persona, isAdmin } = usePersona();
  const { siteId, isFleet } = useActiveSite();
  const location = useLocation();
  const navigate = useNavigate();

  const items = railItemsFor(persona, siteId, isFleet).filter(item => !item.adminOnly || isAdmin);
  const hasOptimizationItem = items.some(item => item.section === 'optimization');

  const { approvals } = useApprovals({
    siteId: HITL_SITE_ID,
    approvalType: HITL_APPROVAL_TYPE,
    enabled: hasOptimizationItem,
  });
  const pendingApprovalsCount = approvals.filter(a => a.state === 'PENDING').length;

  return (
    <nav className="helios-rail" aria-label="Primary">
      {items.map(item => {
        const Icon = item.icon;
        const active =
          location.pathname === item.match || location.pathname.startsWith(item.match + '/');
        const badgeCount = item.section === 'optimization' ? pendingApprovalsCount : 0;
        return (
          <button
            key={item.section}
            type="button"
            className={`helios-rail-item${active ? ' active' : ''}`}
            aria-current={active ? 'page' : undefined}
            title={item.label}
            onClick={() => navigate(item.to)}
          >
            <span className="helios-rail-icon">
              <Icon size={20} />
              {badgeCount > 0 && (
                <span className="helios-rail-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
              )}
            </span>
            <span className="label">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
